import React, { useCallback, useState } from 'react';
import { ArrowLeftIcon, MapPinIcon, Building2Icon, ChevronRightIcon, EyeIcon } from 'lucide-react';
import { User, Store, BookingSlot, AppView, SlotStatus, MediaType, BrandRequest } from '../data/types';
import { Header } from '../components/layout/Header';
import { Badge } from '../components/ui/Badge';
import { TimelineGrid } from '../components/timeline/TimelineGrid';
import { BookingPanel } from '../components/booking/BookingPanel';
import { GlobalWeekBookingPanel } from '../components/booking/GlobalWeekBookingPanel';
import { PageTransition } from '../components/ui/PageTransition';
interface StoreTimelinePageProps {
  user: User;
  store: Store;
  allStores: Store[];
  brandRequests: BrandRequest[];
  onLogout: () => void;
  onBack: () => void;
  onNavigate: (view: AppView) => void;
  onUpdateSlot: (storeId: string, slotId: string, updates: Partial<BookingSlot>) => void;
}
export function StoreTimelinePage({
  user,
  store,
  allStores,
  brandRequests,
  onLogout,
  onBack,
  onNavigate,
  onUpdateSlot
}: StoreTimelinePageProps) {
  const [selectedSlot, setSelectedSlot] = useState<BookingSlot | null>(null);
  const [weekSlots, setWeekSlots] = useState<BookingSlot[]>([]);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  // Global week booking panel state
  const [isGlobalPanelOpen, setIsGlobalPanelOpen] = useState(false);
  const [globalWeekNumber, setGlobalWeekNumber] = useState<number>(1);
  const isAdmin = user.role === 'admin';
  const closePanel = useCallback(() => {
    setIsPanelOpen(false);
    setTimeout(() => {
      setSelectedSlot(null);
      setWeekSlots([]);
    }, 300);
  }, []);
  // Single slot click
  const handleSlotClick = useCallback((slot: BookingSlot) => {
    if (slot.status === 'available' || isAdmin && (slot.status === 'blocked' || slot.status === 'booked' || slot.status === 'internal')) {
      setWeekSlots([]);
      setSelectedSlot(slot);
      setIsPanelOpen(true);
    }
  }, [isAdmin]);
  // Week header click — open global panel for all stores
  const handleWeekClick = useCallback((weekNumber: number) => {
    setGlobalWeekNumber(weekNumber);
    setIsGlobalPanelOpen(true);
  }, []);
  // Global panel confirm — update slots globally via parent callback
  const handleGlobalConfirm = useCallback((selections: {
    storeId: string;
    mediaType: MediaType;
    slotId: string;
  }[], bookingMode: 'brand' | 'internal', brandName?: string, campaignName?: string) => {
    selections.forEach(({
      storeId,
      slotId
    }) => {
      if (bookingMode === 'internal') {
        onUpdateSlot(storeId, slotId, {
          status: 'internal',
          isInternal: true,
          internalCampaignName: campaignName || 'Woolworths'
        });
      } else {
        onUpdateSlot(storeId, slotId, {
          status: 'booked',
          brandName: brandName || 'Woolworths Booking',
          internalCampaignName: undefined
        });
      }
    });
    setIsGlobalPanelOpen(false);
  }, [onUpdateSlot]);
  // Single slot confirm
  const handleConfirmBooking = useCallback((notes?: string, brandName?: string, campaignName?: string) => {
    if (!selectedSlot) return;
    onUpdateSlot(store.id, selectedSlot.id, {
      status: 'booked',
      brandName: brandName || 'Woolworths Booking',
      internalCampaignName: campaignName || undefined
    });
    closePanel();
  }, [selectedSlot, store.id, onUpdateSlot, closePanel]);
  // Multi-slot confirm
  const handleConfirmMultiple = useCallback((slotIds: string[], notes?: string, brandName?: string, campaignName?: string) => {
    slotIds.forEach(id => {
      if (campaignName) {
        onUpdateSlot(store.id, id, {
          status: 'internal',
          isInternal: true,
          internalCampaignName: campaignName
        });
      } else {
        onUpdateSlot(store.id, id, {
          status: 'booked',
          brandName: brandName || 'Woolworths Booking',
          internalCampaignName: undefined
        });
      }
    });
    closePanel();
  }, [store.id, onUpdateSlot, closePanel]);
  const handleRemoveAvailability = useCallback((slot: BookingSlot) => {
    onUpdateSlot(store.id, slot.id, {
      status: 'blocked'
    });
    closePanel();
  }, [store.id, onUpdateSlot, closePanel]);
  const handleBookInternal = useCallback((slot: BookingSlot, notes?: string, campaignName?: string) => {
    onUpdateSlot(store.id, slot.id, {
      status: 'internal',
      isInternal: true,
      internalCampaignName: campaignName || 'Woolworths'
    });
    closePanel();
  }, [store.id, onUpdateSlot, closePanel]);
  const handleUnblockToAvailable = useCallback((slot: BookingSlot) => {
    onUpdateSlot(store.id, slot.id, {
      status: 'available',
      brandName: undefined
    });
    closePanel();
  }, [store.id, onUpdateSlot, closePanel]);
  return <div className="min-h-screen w-full bg-white flex flex-col" data-id="element-2061">
      <Header user={user} onLogout={onLogout} currentView="store-timeline" onNavigate={onNavigate} data-id="element-2062" />
      <PageTransition className="flex-1 flex flex-col" data-id="element-2063">
        {/* Breadcrumb navigation */}
        <div className="px-6 py-3 border-b border-border bg-white flex items-center gap-2 text-sm" data-id="element-2064">
          <button onClick={onBack} className="text-text-secondary hover:text-text-primary transition-colors font-medium" data-id="element-2065">
            Dashboard
          </button>
          <ChevronRightIcon className="w-4 h-4 text-text-tertiary" data-id="element-2066" />
          <span className="text-text-primary font-semibold" data-id="element-2067">{store.name}</span>
        </div>

        {/* Store header */}
        <div className="px-6 py-6 border-b border-border bg-surface-warm" data-id="element-2068">
          <div className="flex items-start justify-between" data-id="element-2069">
            <div data-id="element-2070">
              <h1 className="text-2xl font-bold text-text-primary mb-2" data-id="element-2071">
                {store.name}
              </h1>
              <div className="flex items-center gap-4 text-sm text-text-secondary" data-id="element-2072">
                <div className="flex items-center gap-1.5" data-id="element-2073">
                  <MapPinIcon className="w-4 h-4" data-id="element-2074" />
                  {store.location}
                </div>
                <div className="flex items-center gap-1.5" data-id="element-2075">
                  <Building2Icon className="w-4 h-4" data-id="element-2076" />
                  {store.province}
                </div>
              </div>
            </div>
            <Badge variant={store.tier} className="text-xs" data-id="element-2077">
              {store.tier.replace('-', ' ')}
            </Badge>
          </div>
        </div>

        {/* Timeline section */}
        <div className="flex-1 p-6 bg-surface overflow-auto" data-id="element-2078">
          <div className="mb-4 flex items-start justify-between" data-id="element-2079">
            <div data-id="element-2080">
              <h2 className="text-sm font-semibold text-text-primary uppercase tracking-wide" data-id="element-2081">
                12-Week Availability Timeline
              </h2>
              <p className="text-xs text-text-tertiary mt-1" data-id="element-2082">
                {isAdmin ? 'Click on slots to manage bookings, or click a week header to book across all stores' : 'View availability across all media types for this store'}
              </p>
            </div>
            {!isAdmin && <div className="flex items-center gap-1.5 px-3 py-1.5 bg-surface-warm border border-border rounded-sm text-xs font-medium text-text-secondary" data-id="element-2083">
                <EyeIcon className="w-3.5 h-3.5" data-id="element-2084" />
                Read Only
              </div>}
          </div>

          <TimelineGrid store={store} isAdmin={isAdmin} brandRequests={brandRequests} onSlotClick={handleSlotClick} onWeekClick={isAdmin ? handleWeekClick : undefined} data-id="element-2085" />
        </div>

        {/* Single-slot Booking Panel */}
        <BookingPanel slot={selectedSlot} weekSlots={weekSlots.length > 0 ? weekSlots : undefined} isOpen={isPanelOpen} user={user} onClose={closePanel} onConfirm={handleConfirmBooking} onConfirmMultiple={handleConfirmMultiple} onRemoveAvailability={handleRemoveAvailability} onBookInternal={handleBookInternal} onUnblockToAvailable={handleUnblockToAvailable} data-id="element-2086" />
        {/* Global Week Booking Panel */}
        <GlobalWeekBookingPanel isOpen={isGlobalPanelOpen} weekNumber={globalWeekNumber} currentStoreId={store.id} stores={allStores} onClose={() => setIsGlobalPanelOpen(false)} onConfirm={handleGlobalConfirm} data-id="element-2087" />
      </PageTransition>
    </div>;
}